import { ChangeDetectionStrategy, Component, ViewEncapsulation, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { DxPopupModule } from 'devextreme-angular/ui/popup';
import { DxScrollViewModule } from 'devextreme-angular/ui/scroll-view';

import { BulkImportSession, BulkImportSessionService } from 'app/core/services/bulk-import-session.service';
import { ShipmentBulkImportPanelComponent } from 'app/modules/shipment/shipment-import/shipment-bulk-import/shipment-bulk-import-panel.component';

/**
 * Các cửa sổ nhập lô hàng loạt, dựng ở LAYOUT chứ không trong trang.
 *
 * Đây là toàn bộ điểm của component này: nằm ngoài `router-outlet` nên đổi route không huỷ panel, và
 * người dùng thu nhỏ cửa sổ rồi đi làm việc khác trong lúc vài chục lô đang chạy. Trang
 * `shipment-import-page` giờ chỉ còn việc CHỌN TỆP rồi mở lượt qua `BulkImportSessionService`.
 *
 * Nhiều lượt cùng sống nên nhiều cửa sổ cùng được dựng, nhưng service giữ bất biến "nhiều nhất một cửa
 * sổ đang hiện" — phần còn lại nằm im ở dạng viên. Panel của lượt bị cất đi KHÔNG bị huỷ, đó là lý do
 * lượt đó vẫn chạy tiếp.
 *
 * `@defer` là bắt buộc, không phải tối ưu cho vui: panel kéo theo cả cây dịch vụ của module shipment
 * (báo giá, nhãn, hoá đơn, exceljs...). Nhập nó thẳng vào layout — thứ nạp ở MỌI trang — là ném toàn bộ
 * khối đó vào bundle khởi động. Với `@defer`, panel chỉ được tải khi thực sự có lượt nhập.
 */
@Component({
    selector: 'bulk-import-host',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    templateUrl: './bulk-import-host.component.html',
    // Mọi thứ ở đây CHỈ được dùng bên trong khối `@defer` — đó là điều kiện để trình biên dịch Angular
    // đổi chúng thành dynamic import thay vì import tĩnh. Dùng module DevExtreme lẻ thay vì `SharedModule`
    // vì lý do tương tự: `SharedModule` kéo theo hàng loạt thứ layout không cần.
    imports: [DxPopupModule, DxScrollViewModule, ShipmentBulkImportPanelComponent],
})
export class BulkImportHostComponent {
    readonly session = inject(BulkImportSessionService);
    private readonly transloco = inject(TranslocoService);

    /**
     * Tuỳ chọn nút thu nhỏ, MỘT đối tượng cho MỖI lượt và giữ nguyên tham chiếu qua các nhịp vẽ.
     *
     * `dx-popup` so tham chiếu: dựng đối tượng mới ở mỗi nhịp change detection sẽ khiến nó dựng lại
     * toolbar liên tục. Cache theo id vì mỗi cửa sổ cần một `onClick` đóng đúng lượt của nó.
     *
     * Dựng ở lần đọc đầu chứ không phải lúc khởi tạo class: component này thuộc layout nên được tạo ngay
     * lúc mở app, thời điểm gói ngôn ngữ có thể chưa tải xong — `translate()` lúc đó trả về chính khoá.
     */
    private readonly minimizeOptionsById = new Map<string, Record<string, unknown>>();

    minimizeOptions(id: string): Record<string, unknown> {
        let options = this.minimizeOptionsById.get(id);
        if (!options) {
            options = {
                icon: 'minus',
                stylingMode: 'text',
                hint: this.transloco.translate('shipment-import.bulk.dock.minimize'),
                onClick: (): void => this.session.minimize(id),
            };
            this.minimizeOptionsById.set(id, options);
        }
        return options;
    }

    /**
     * Tên tệp đi kèm tiêu đề: với nhiều lượt cùng chạy thì "Nhập lô hàng theo template" trên cả ba cửa
     * sổ không nói được cửa sổ nào đang mở.
     */
    popupTitle(item: BulkImportSession): string {
        const base = this.transloco.translate('shipment-import.bulk.popup-title');
        return item.File?.name ? `${base} — ${item.File.name}` : base;
    }

    /**
     * `dx-popup` phát `visibleChange(false)` cho BA việc khác hẳn nhau: người dùng bấm ×, người dùng bấm
     * −, và lượt này bị thu nhỏ vì một lượt khác vừa được bung ra. Chỉ việc ĐẦU mới được đóng lượt.
     *
     * Đọc trạng thái từ service chứ không tin `item` mà template truyền vào: `item` là ảnh chụp của
     * `@for` tại nhịp vẽ, còn sự kiện của DevExtreme bắn ra từ trong lòng lời gọi ghi option. Nếu hai
     * thứ đó lệch nhau một nhịp thì một lượt vừa bị thu nhỏ sẽ bị hiểu thành "người dùng vừa đóng" và
     * xoá sạch — im lặng, không lỗi, không hoàn tác được.
     */
    onVisibleChange(item: BulkImportSession, visible: boolean): void {
        if (visible) return;

        const current = this.session.sessions().find((session) => session.Id === item.Id);
        if (!current || current.Minimized) return;

        this.minimizeOptionsById.delete(item.Id);
        this.session.close(item.Id);
    }
}
